<!-- eslint-disable no-unused-vars -->
<template>
  <div class="mb-4 mx-1">
    <div class="relative">
      <label
        v-if="label != ''"
        class="block mb-2 text-sm font-medium text-textBlack1 dark:text-textWhite1 transition-colors duration-200"
      >
        {{ label }}
        <span v-if="required" class="text-red-500 ml-1">*</span>
      </label>
      <div v-if="label == ''" class="h-4"></div>
      <div class="relative">
        <input
          :type="type"
          :value="modelValue"
          @input="checkInput"
          @focus="handleFocus"
          @blur="handleBlur"
          v-bind="$attrs"
          :placeholder="placeholder"
          :class="[
            inputClasses,
            $slots.prefix ? 'pl-10' : '',
            $slots.suffix ? 'pr-10' : ''
          ]"
          :readonly="!isEditable"
          :disabled="disabled"
        />
        
        <!-- Icon slot for prefix icons -->
        <div v-if="$slots.prefix" class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <slot name="prefix" />
        </div>
        
        <!-- Icon slot for suffix icons -->
        <div v-if="$slots.suffix" class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
          <slot name="suffix" />
        </div>
      </div>

      <!-- Error message -->
      <div v-if="isError && errorMsg" class="error-message mt-2 flex items-center text-red-500 text-sm">
        <i class="fa fa-exclamation-circle mr-2"></i>
        {{ errorMsg }}
      </div>
      
      <!-- Helper text -->
      <div v-if="helperText && !isError" class="helper-text mt-2 text-gray-500 dark:text-gray-400 text-sm">
        {{ helperText }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineProps, defineEmits, ref, computed } from "vue";

const props = defineProps({
  label: {
    type: String,
    default: "",
  },
  placeholder: {
    type: String,
    default: "",
  },
  modelValue: {
    type: [String, Number],
    default: "",
  },
  isError: {
    type: Boolean,
    default: false,
  },
  errorMsg: {
    type: String,
    default: "",
  },
  type: {
    type: String,
    default: "text",
  },
  isEditable: {
    type: Boolean,
    default: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  required: {
    type: Boolean,
    default: false,
  },
  helperText: {
    type: String,
    default: "",
  },
  size: {
    type: String,
    default: "md",
    validator: (value) => ["sm", "md", "lg"].includes(value),
  },
  variant: {
    type: String,
    default: "default",
    validator: (value) => ["default", "filled", "outlined"].includes(value),
  }
});

const emit = defineEmits(["update:isError", "update:modelValue", "focus", "blur"]);

const isFocused = ref(false);

// Size classes
const sizeClasses = computed(() => {
  const sizes = {
    sm: "px-3 py-2 text-sm",
    md: "px-4 py-2.5 text-sm",
    lg: "px-4 py-3 text-base",
  };
  return sizes[props.size];
});

// Variant classes
const variantClasses = computed(() => {
  const variants = {
    default: "bg-white dark:bg-bgFullDark border border-gray-300 dark:border-gray-600",
    filled: "bg-gray-50 dark:bg-gray-700 border-0",
    outlined: "bg-transparent border-2 border-gray-300 dark:border-gray-600",
  };
  return variants[props.variant];
});

// Compute input classes based on props and state
const inputClasses = computed(() => {
  const baseClasses = [
    "block w-full rounded-lg transition-all duration-200 ease-in-out",
    "focus:outline-none focus:ring-2 focus:ring-opacity-50",
    "disabled:opacity-50 disabled:cursor-not-allowed",
    "read-only:bg-gray-50 dark:read-only:bg-gray-800 read-only:cursor-default",
    sizeClasses.value,
    variantClasses.value,
  ];

  // Error state
  if (props.isError) {
    baseClasses.push(
      "border-red-500 text-red-500 focus:ring-red-500",
      "dark:border-red-600 dark:text-red-500"
    );
  } else {
    // Normal state
    baseClasses.push(
      "text-textBlack1 dark:text-white",
      "placeholder-gray-400 dark:placeholder-gray-200",
      "focus:ring-blue-500 focus:border-blue-500"
    );
  }

  // Focus state
  if (isFocused.value && !props.isError) {
    baseClasses.push("ring-2 ring-blue-500 ring-opacity-20");
  }

  // Disabled state
  if (props.disabled) {
    baseClasses.push("bg-gray-100 dark:bg-gray-800 cursor-not-allowed");
  }

  // Icon padding adjustments will be handled in template

  return baseClasses.join(" ");
});

// Event handlers
const checkInput = (e) => {
  emit("update:modelValue", e.target.value);
};

const handleFocus = (e) => {
  isFocused.value = true;
  emit("focus", e);
};

const handleBlur = (e) => {
  isFocused.value = false;
  emit("blur", e);
};
</script>

<style >
/* Custom focus ring animation */
input:focus {
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Smooth transitions for all states */
input {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Error state animation */
input.error {
  animation: shake 0.5s ease-in-out;
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-4px); }
  75% { transform: translateX(4px); }
}

/* Custom scrollbar for textarea-like inputs */
input::-webkit-scrollbar {
  width: 4px;
}

input::-webkit-scrollbar-track {
  background: transparent;
}

input::-webkit-scrollbar-thumb {
  background: rgba(156, 163, 175, 0.3);
  border-radius: 2px;
}

input::-webkit-scrollbar-thumb:hover {
  background: rgba(156, 163, 175, 0.5);
}

/* Dark mode scrollbar */
.dark input::-webkit-scrollbar-thumb {
  background: rgba(75, 85, 99, 0.3);
}

.dark input::-webkit-scrollbar-thumb:hover {
  background: rgba(75, 85, 99, 0.5);
}

/* Placeholder styling */
input::placeholder {
  transition: color 0.2s ease-in-out;
}

input:focus::placeholder {
  color: rgba(156, 163, 175, 0.7);
}

/* Icon styling */
.prefix-icon,
.suffix-icon {
  transition: color 0.2s ease-in-out;
}

/* Label animation */
label {
  transition: all 0.2s ease-in-out;
}

/* Error message animation */
.error-message {
  animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Helper text styling */
.helper-text {
  transition: all 0.2s ease-in-out;
}

/* Disabled state styling */
input:disabled {
  background-color: rgba(243, 244, 246, 0.5);
  color: rgba(107, 114, 128, 0.7);
  cursor: not-allowed;
}

.dark input:disabled {
  background-color: rgba(31, 41, 55, 0.5);
  color: rgba(156, 163, 175, 0.7);
}

/* Read-only state styling */
input[readonly] {
  background-color: rgba(249, 250, 251, 0.8);
  cursor: default;
}

.dark input[readonly] {
  background-color: rgba(31, 41, 55, 0.8);
}
</style>
